import { Lock } from 'lucide-react';
import { useLicence } from '@/services/queries';
import type { LicenceSeat } from '@/types';
import { StatusPill, toneFor } from '@/shared/ui/status-pill';
import { Skeleton } from '@/shared/ui/skeleton';
import { OverflowTooltip } from '@/shared/ui/overflow-tooltip';
import { cn } from '@/lib/utils';

function initials(seat: LicenceSeat): string {
  if (!seat.holderName) return '—';
  const parts = seat.holderName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '—';
}

/** The design's mono column head. */
function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'border-b border-border px-5 py-3.5 text-left font-mono text-[10.5px] font-semibold tracking-[0.055em] text-muted-foreground uppercase',
        className,
      )}
    >
      {children}
    </th>
  );
}

/** The 3px left mark, coloured by the seat's status. */
const MARK: Record<string, string> = {
  positive: 'border-l-pill-positive-foreground',
  caution: 'border-l-pill-caution-foreground',
  negative: 'border-l-pill-negative-foreground',
  info: 'border-l-pill-info-foreground',
  inert: 'border-l-transparent',
};

export function TeamRoster() {
  const query = useLicence();

  if (query.isPending) return <Skeleton className="h-72 w-full rounded-2xl" />;

  const licence = query.data?.licence ?? null;
  if (!licence) return null;

  return (
    <div className="min-w-0 overflow-hidden rounded-2xl border border-border bg-card shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-6.5 py-5">
        <h2 className="text-base font-bold">Team roster</h2>
        <span className="rounded-[5px] bg-muted px-2 py-0.75 font-mono text-[10.5px] text-muted-foreground">
          {String(licence.seatsClaimed)} / {String(licence.seatCount)} SEATS BOUND
        </span>
        <div className="flex-1" />
        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap text-muted-foreground">
          <Lock size={11} strokeWidth={2.2} aria-hidden="true" />
          Read-only
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-muted/40">
              <Th className="px-6.5">Member</Th>
              <Th>Seat</Th>
              <Th>Fingerprint</Th>
              <Th>Activated</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {licence.seats.map((seat, index) => (
              <tr
                key={seat.id}
                className={cn(
                  'border-b border-l-[3px] border-b-border',
                  MARK[toneFor(seat.status)],
                  index % 2 === 1 && 'bg-muted/20',
                )}
              >
                {/* Name and email capped and ellipsised - a long address would
                    otherwise push every other column off the card. The full
                    value shows on hover. */}
                <OverflowTooltip>
                  <td className="px-6.5 py-4">
                    <div className="flex items-center gap-2.5">
                      <span
                        className="flex size-7 flex-none items-center justify-center rounded-[9px] bg-primary text-[10.5px] font-semibold text-primary-foreground"
                        aria-hidden="true"
                      >
                        {initials(seat)}
                      </span>
                      <div className="min-w-0">
                        <div className="max-w-[240px] truncate text-[12.5px] font-medium">
                          {seat.holderName ?? 'Unassigned'}
                          {seat.isYou && (
                            <span className="ml-1.5 text-[10.5px] text-muted-foreground">
                              (you)
                            </span>
                          )}
                        </div>
                        <div className="max-w-[240px] truncate text-[10.5px] text-muted-foreground">
                          {seat.email}
                        </div>
                      </div>
                    </div>
                  </td>
                </OverflowTooltip>
                <td className="px-5 py-4 font-mono text-xs whitespace-nowrap text-muted-foreground">
                  Seat {String(seat.number)}
                </td>
                <td className="px-5 py-4">
                  {/* Verified, Released or Pending - a state a person can act
                      on, never the hash itself. */}
                  <StatusPill status={seat.fingerprint} minWidth={92} />
                </td>
                <td className="px-5 py-4 text-[12.5px] whitespace-nowrap text-muted-foreground">
                  {seat.activatedAt
                    ? new Date(seat.activatedAt).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '—'}
                </td>
                <td className="px-5 py-4">
                  <StatusPill status={seat.status} minWidth={92} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-start gap-2.5 bg-muted/40 px-6.5 py-4.5">
        <Lock size={14} strokeWidth={1.9} className="mt-px flex-none text-muted-foreground" />
        <p className="text-[11px] leading-[1.6] text-muted-foreground">
          Read-only for everyone, including the owner. Seat reassignment and revocation are handled
          by NuDent support.
        </p>
      </div>
    </div>
  );
}
